import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom'; // Force rebuild 2026-01-22
import { supabase } from '@/integrations/supabase/client';
import SiteHomePage from './public/SiteHomePage';

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  default_town_id: string | null;
  favicon_url: string | null;
  primary_domain: string | null;
  tagline: string | null;
  description: string | null;
  theme: string;
  use_home_page: boolean;
  realtor_assigned: boolean;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  seller_ad_image_url: string | null;
  buyer_ad_image_url: string | null;
  mortgage_ad_image_url: string | null;
  enable_market_value_report: boolean;
  enable_community_guide: boolean;
  enable_mortgage_info: boolean;
  bing_auth_code: string | null;
}

interface TownData {
  id: string;
  slug: string;
}

const Index = () => {
  const [site, setSite] = useState<SiteData | null>(null);
  const [defaultTown, setDefaultTown] = useState<TownData | null>(null);
  const [loading, setLoading] = useState(true);
  const envSupabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  useEffect(() => {
    // Remove the static SEO fallback content once React has mounted
    const fallback = document.getElementById('seo-fallback');
    if (fallback) {
      fallback.remove();
    }
    
    checkForPublicSite();
  }, []);

  const checkForPublicSite = async () => {
    try {
      const hostname = window.location.hostname;
      const debug = new URLSearchParams(window.location.search).get('debug') === '1';
      
      // Check if this is a development/preview domain
      const isDevelopment = 
        hostname === 'localhost' || 
        hostname.includes('lovable.app') ||
        hostname.includes('127.0.0.1');

      if (debug) {
        console.log('[Index] debug=1', {
          hostname,
          isDevelopment,
          envSupabaseUrlPresent: !!envSupabaseUrl,
        });
      }

      let siteData: SiteData | null = null;
      let siteId: string | null = null;

      // Step 1: Resolve site ID quickly
      if (!isDevelopment) {
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const hostWithWww = hostname.startsWith('www.') ? hostname : `www.${hostname}`;

        const { data: domainMatch } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          // Match on primary_domain, and also allow canonical_base_url to be the source of truth
          // (common when primary_domain is set without www, but canonical_base_url includes www).
          .or(
            `primary_domain.eq.${hostname},` +
              `primary_domain.eq.${hostWithoutWww},` +
              `primary_domain.eq.${hostWithWww},` +
              `canonical_base_url.ilike.%${hostname}%,` +
              `canonical_base_url.ilike.%${hostWithoutWww}%`
          )
          .limit(1)
          .maybeSingle();

        if (debug) {
          console.log('[Index] domainMatch', {
            hostname,
            hostWithoutWww,
            hostWithWww,
            domainMatch,
          });
        }

        if (domainMatch) {
          siteId = (domainMatch as any).id;
        }
      }

      // Development/Preview: Check if user is logged in and use their site
      if (!siteId && isDevelopment) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: siteAdmin } = await supabase
              .from('site_admins')
              .select('site_id')
              .eq('user_id', user.id)
              .maybeSingle();

            if (siteAdmin) {
              siteId = siteAdmin.site_id;
            }
          }
        } catch (authError) {
          console.log('Auth check failed, using fallback:', authError);
        }
      }

      // Final fallback: Get first active site by creation date
      if (!siteId) {
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (debug) console.log('[Index] fallbackSite', fallbackSite);

        siteId = (fallbackSite as any)?.id || null;
      }

      // Step 2: Fetch full site data once we have the ID
      if (siteId) {
        const { data: fullSite } = await supabase
          .from('sites' as any)
          .select('id, name, location_served, default_town_id, favicon_url, primary_domain, tagline, description, theme, use_home_page, realtor_assigned, tenant_id, canonical_base_url, header_font, seller_ad_image_url, buyer_ad_image_url, mortgage_ad_image_url, enable_market_value_report, enable_community_guide, enable_mortgage_info, bing_auth_code')
          .eq('id', siteId)
          .eq('status', 'active')
          .maybeSingle();

        if (debug) console.log('[Index] fullSite', fullSite);

        siteData = fullSite as unknown as SiteData;
      }

      if (siteData) {
        // Ensure crawlers see tenant-specific metadata as early as possible
        try {
          // Title format: "Website Name | Location" (e.g., "Crippin Real Estate | South Coast, MA")
          const siteName = siteData.name || 'Real Estate';
          const title = siteData.location_served 
            ? `${siteName} | ${siteData.location_served}`
            : siteName;
          document.title = title;

          // Description priority: description field > tagline > auto-generated
          const description = siteData.description 
            || siteData.tagline 
            || `Find your perfect home with ${siteName}. Expert real estate services in ${siteData.location_served}.`;
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) metaDesc.setAttribute('content', description);

          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) ogTitle.setAttribute('content', title);
          const ogDesc = document.querySelector('meta[property="og:description"]');
          if (ogDesc) ogDesc.setAttribute('content', description);
          const ogSite = document.querySelector('meta[property="og:site_name"]');
          if (ogSite) ogSite.setAttribute('content', title);

          const twTitle = document.querySelector('meta[name="twitter:title"]');
          if (twTitle) twTitle.setAttribute('content', title);
          const twDesc = document.querySelector('meta[name="twitter:description"]');
          if (twDesc) twDesc.setAttribute('content', description);
        } catch (_) {
          // no-op
        }

        setSite(siteData);

        // If not using home page and has a default town, fetch it for redirect
        if (!siteData.use_home_page && siteData.default_town_id) {
          const { data: townData } = await supabase
            .from('towns')
            .select('id, slug')
            .eq('id', siteData.default_town_id)
            .eq('is_published', true)
            .single();

          if (townData) {
            setDefaultTown(townData);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for public site:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    // Return minimal SEO-friendly content while loading to prevent "Loading..." from being indexed
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="sr-only">
          <h1>Local Real Estate Services</h1>
          <p>Your trusted local real estate resource. Loading property information...</p>
        </div>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-hidden="true"></div>
      </div>
    );
  }

  // No active site found: show a friendly message instead of redirecting
  if (!site) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background">
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Site not found</h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Host: <span className="font-mono">{window.location.hostname}</span>
          </p>
          {!envSupabaseUrl ? (
            <p className="mt-4 text-sm text-muted-foreground">
              This deployment is missing its backend environment configuration.
            </p>
          ) : null}
          <p className="mt-3 text-muted-foreground">
            This domain isn’t currently linked to an active site. If you’re the site owner, please sign in to the
            admin area to confirm domain settings.
          </p>
          <a
            href="/admin/login"
            className="inline-flex mt-8 items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
          >
            Go to admin login
          </a>
        </div>
      </main>
    );
  }

  // If not using home page and has a default town, redirect to that town
  if (!site.use_home_page && defaultTown) {
    return <Navigate to={`/${defaultTown.slug}`} replace />;
  }

  // Show the home page
  return <SiteHomePage site={site} />;
};

export default Index;
